import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  LinearProgress,
  Chip,
  Paper,
  Fade,
  Alert,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { useAssessment } from '../../contexts/AssessmentContext';
import { useLocale } from '../../contexts/LocaleContext';
import { sessionAPI } from '../../services/api';
import {
  trackQuestionAnswered,
  trackQuestionnaireComplete,
  trackParticipantFormView,
  trackParticipantFormSubmit,
  trackAssessmentSubmit,
} from '../../utils/analytics';

// Participant info interface
interface ParticipantInfo {
  name: string;
  email: string;
  phone?: string;
  organization: string;
  position?: string;
  address?: string;
  employeeCount?: string;
}

interface Question {
  id: string;
  text: string;
  type: 'radio' | 'text';
  options?: { value: string; label: string }[];
  required: boolean;
}

interface SingleQuestionPageProps {
  assessment: any;
  sessionReady?: boolean;  // Session creates in background
  onComplete: () => void;
}

// Validation schema for participant form
const createValidationSchema = (t: (key: string) => string) => yup.object({
  name: yup.string().required(t('validation.required')),
  email: yup.string().email(t('validation.email')).required(t('validation.required')),
  phone: yup.string(),
  organization: yup.string().required(t('validation.required')),
  position: yup.string(),
  address: yup.string(),
  employeeCount: yup.string(),
});

const SingleQuestionPage: React.FC<SingleQuestionPageProps> = ({
  assessment,
  sessionReady = true,
  onComplete,
}) => {
  const { state, dispatch } = useAssessment();
  const { t } = useLocale();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showParticipantForm, setShowParticipantForm] = useState(false);
  const [pendingResponses, setPendingResponses] = useState<any[]>([]);

  // Get pre-filled email from campaign URL (if exists)
  const campaignEmail = typeof window !== 'undefined'
    ? sessionStorage.getItem('campaign_email') || ''
    : '';

  // Get pre-filled info from quiz lead (if coming from qualifying quiz)
  const quizLeadName = typeof window !== 'undefined' ? sessionStorage.getItem('quiz_lead_name') || '' : '';
  const quizLeadEmail = typeof window !== 'undefined' ? sessionStorage.getItem('quiz_lead_email') || '' : '';
  const quizLeadPhone = typeof window !== 'undefined' ? sessionStorage.getItem('quiz_lead_phone') || '' : '';
  const quizLeadEmployees = typeof window !== 'undefined' ? sessionStorage.getItem('quiz_lead_employees') || '' : '';

  // Form for participant info
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<ParticipantInfo>({
    resolver: yupResolver(createValidationSchema(t)) as any,
    defaultValues: {
      name: quizLeadName || '',
      email: quizLeadEmail || campaignEmail,
      phone: quizLeadPhone || '',
      organization: '',
      position: 'owner',
      address: '',
      employeeCount: quizLeadEmployees || '',
    },
  });

  // Flatten all questions from all pages
  const allQuestions: Question[] = assessment.questions.flatMap((page: any) => 
    page.questions.map((question: any) => ({
      ...question,
      options: question.options?.map((option: any) => ({
        ...option,
        label: option.value === '+PLUS' ? t('answer.positive') :
               option.value === 'Maybe' ? t('answer.neutral') :
               option.value === '-MINUS' ? t('answer.negative') :
               option.label
      }))
    }))
  );


  const currentQuestion = allQuestions[currentQuestionIndex];
  const totalQuestions = allQuestions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  // Submit all responses mutation - now includes participant info
  const submitAllResponsesMutation = useMutation(
    ({ responses, participantInfo }: { responses: any[], participantInfo: ParticipantInfo }) => {
      if (!state.currentSessionId) throw new Error('No session ID');

      return sessionAPI.submitResponses(state.currentSessionId, {
        responses: responses,
        participantInfo: {
          name: participantInfo.name,
          email: participantInfo.email,
          phone: participantInfo.phone,
          organization: participantInfo.organization,
          position: participantInfo.position,
          address: participantInfo.address,
          employeeCount: participantInfo.employeeCount,
        },
      });
    },
    {
      onSuccess: () => {
        toast.success(t('toast.assessment.completed'));
        onComplete();
      },
      onError: (error: any) => {
        console.error('Final submission error:', error);
        toast.error(t('toast.submission.error'));
      },
    }
  );

  // Handle participant form submission
  const onParticipantSubmit = (data: ParticipantInfo) => {
    // Session should be ready by now (created in background while user answered questions)
    if (!state.currentSessionId) {
      toast.error(t('toast.session.preparing') || 'Preparing session...');
      return;
    }

    // Track participant form submission
    trackParticipantFormSubmit(
      !!data.phone,
      !!data.address,
      data.position || 'unknown'
    );

    // Track assessment submission
    trackAssessmentSubmit(state.currentSessionId, '360', pendingResponses.length);

    // Store participant info in context
    dispatch({ type: 'SET_PARTICIPANT_INFO', payload: data });

    // Submit responses with participant info
    submitAllResponsesMutation.mutate({
      responses: pendingResponses,
      participantInfo: data,
    });
  };

  // Define handleFinalSubmission - now shows participant form instead of submitting
  const handleFinalSubmission = useCallback((lastAnswer: string) => {
    // Create a complete responses object including the last answer
    const completeResponses = {
      ...state.responses,
      [currentQuestion.id]: lastAnswer
    };

    // Check for missing answers
    const missingQuestions: string[] = [];
    const allResponses = allQuestions.map(question => {
      const answer = completeResponses[question.id];
      if (!answer) {
        console.error(`Missing answer for question ${question.id}`);
        missingQuestions.push(question.id);
        return null;
      }
      return {
        questionId: question.id,
        answer: answer,
        questionText: question.text || ''
      };
    }).filter(response => response !== null);

    // Validate all questions are answered
    if (missingQuestions.length > 0) {
      const message = t('validation.missing.answers').replace('{{count}}', missingQuestions.length.toString());
      toast.error(message);
      console.error('Missing answers for questions:', missingQuestions);
      return;
    }

    // Double-check we have all answers
    if (allResponses.length !== allQuestions.length) {
      toast.error(t('validation.incomplete.assessment'));
      return;
    }

    // Store responses and show participant form
    setPendingResponses(allResponses);
    dispatch({ type: 'SET_QUESTIONNAIRE_COMPLETE', payload: true });
    setShowParticipantForm(true);

    // Track questionnaire completion
    trackQuestionnaireComplete(allQuestions.length, '360');
    trackParticipantFormView();
  }, [state.responses, currentQuestion.id, allQuestions, dispatch, t]);

  // Define handleSubmit - now saves locally and moves to next question
  const handleSubmit = useCallback((answer?: string) => {
    const finalAnswer = answer || selectedAnswer;
    
    if (!finalAnswer) {
      toast.error(t('toast.select.answer'));
      return;
    }

    // Update context (save locally) - this happens immediately
    dispatch({
      type: 'UPDATE_RESPONSE',
      payload: { questionId: currentQuestion.id, answer: finalAnswer },
    });

    // Track question answered (for milestones)
    trackQuestionAnswered(currentQuestionIndex, totalQuestions, '360');

    // Move to next question or complete assessment
    if (currentQuestionIndex + 1 >= totalQuestions) {
      // This is the last question - submit everything
      setTimeout(() => handleFinalSubmission(finalAnswer), 100); // Small delay to ensure state update
    } else {
      // Move to next question with reduced delay for faster UX
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
        setSelectedAnswer('');
        setIsTransitioning(false);
      }, 150); // Reduced from 300ms to 150ms
    }
  }, [selectedAnswer, currentQuestion, currentQuestionIndex, totalQuestions, dispatch, t, handleFinalSubmission]);

  // Add state to prevent double-clicking
  const [isProcessing, setIsProcessing] = useState(false);
  
  const handleAnswerSelect = useCallback((value: string) => {
    // Prevent double-clicking/fast clicking
    if (isProcessing) return;
    
    setSelectedAnswer(value);
    setIsProcessing(true);
    
    // Auto-submit with a small delay to ensure state is saved
    setTimeout(() => {
      handleSubmit(value);
      // Reset processing state after a delay
      setTimeout(() => setIsProcessing(false), 300);
    }, 50);
  }, [handleSubmit, isProcessing]);

  const handlePrevious = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev - 1);
        setSelectedAnswer('');
        setIsTransitioning(false);
      }, 150);
    }
  }, [currentQuestionIndex]);

  // Keyboard shortcuts
  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (!currentQuestion?.options || submitAllResponsesMutation.isLoading) return;

    switch (event.key) {
      case '1':
        if (currentQuestion.options[0]?.value) {
          handleAnswerSelect(currentQuestion.options[0].value);
        }
        break;
      case '2':
        if (currentQuestion.options[1]?.value) {
          handleAnswerSelect(currentQuestion.options[1].value);
        }
        break;
      case '3':
        if (currentQuestion.options[2]?.value) {
          handleAnswerSelect(currentQuestion.options[2].value);
        }
        break;
      case 'ArrowLeft':
        if (currentQuestionIndex > 0) {
          handlePrevious();
        }
        break;
    }
  }, [currentQuestion, currentQuestionIndex, submitAllResponsesMutation.isLoading, handleAnswerSelect, handlePrevious]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  // Load existing answer if user goes back
  useEffect(() => {
    const existingAnswer = state.responses[currentQuestion?.id];
    if (existingAnswer) {
      setSelectedAnswer(existingAnswer);
    } else {
      setSelectedAnswer('');
    }
  }, [currentQuestion?.id, state.responses]);

  if (!currentQuestion && !showParticipantForm) {
    return (
      <Alert severity="error">
        {t('assessment.error')}
      </Alert>
    );
  }

  // Show participant info form after all questions are answered
  if (showParticipantForm) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1, md: 2 } }}>
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 1 }}>
              {t('participant.form.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              {t('participant.form.subtitle')}
            </Typography>

            <Box component="form" onSubmit={handleFormSubmit(onParticipantSubmit as any)}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('participant.form.name')}
                    {...register('name')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('participant.form.email')}
                    type="email"
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('participant.form.phone')}
                    {...register('phone')}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('participant.form.organization')}
                    {...register('organization')}
                    error={!!errors.organization}
                    helperText={errors.organization?.message}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.position}>
                    <InputLabel>{t('participant.form.position')}</InputLabel>
                    <Select
                      {...register('position')}
                      label={t('participant.form.position')}
                      defaultValue="owner"
                    >
                      <MenuItem value="owner">{t('participant.form.position.owner')}</MenuItem>
                      <MenuItem value="co-owner">{t('participant.form.position.coowner')}</MenuItem>
                      <MenuItem value="ceo">{t('participant.form.position.ceo')}</MenuItem>
                      <MenuItem value="manager">{t('participant.form.position.manager')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('participant.form.address')}
                    {...register('address')}
                    error={!!errors.address}
                    helperText={errors.address?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('participant.form.employeeCount')}
                    {...register('employeeCount')}
                    error={!!errors.employeeCount}
                    helperText={errors.employeeCount?.message}
                    placeholder="50"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitAllResponsesMutation.isLoading}
                  sx={{ minWidth: 200, py: 1.5 }}
                >
                  {submitAllResponsesMutation.isLoading ? t('participant.form.submitting') : t('participant.form.submit')}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Completion indicator */}
        <Paper elevation={1} sx={{ p: 2, mt: 3, bgcolor: 'success.light' }}>
          <Typography variant="body2" color="success.contrastText" sx={{ textAlign: 'center' }}>
            ✓ {t('question.all.answered', { total: totalQuestions })}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1, md: 2 } }}>

      {/* Main Question Card */}
      <Fade in={!isTransitioning} timeout={300}>
        <Card elevation={3}>
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {/* Question Text */}
            <Typography 
              variant="h5"
              component="h2" 
              gutterBottom
              sx={{ 
                textAlign: 'center', 
                mb: { xs: 3, md: 4 },
                direction: 'rtl',
                fontWeight: { xs: 400, md: 500 },
                color: 'text.primary',
                fontSize: { xs: '1.1rem', sm: '1.3rem', md: '2.125rem' },
                lineHeight: { xs: 1.4, md: 1.2 },
                minHeight: { xs: '6.16rem', sm: 'auto' }, // 4 lines * 1.1rem * 1.4 line-height
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {currentQuestion.text}
            </Typography>

            {/* Answer Options */}
            {currentQuestion.options && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' }, // Vertical on mobile, horizontal on desktop
                justifyContent: 'center', 
                alignItems: { xs: 'stretch', md: 'center' }, // Stretch full width on mobile
                gap: 2, 
                mb: 4,
                px: { xs: 2, md: 0 } // Add padding on mobile
              }}>
                {currentQuestion.options.map((option, index) => (
                  <Paper
                    key={option.value}
                    elevation={selectedAnswer === option.value ? 8 : 2}
                    sx={{
                      p: { xs: 2, md: 3 },
                      minWidth: { xs: 'auto', md: 150 },
                      width: { xs: '100%', md: 'auto' }, // Full width on mobile
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      bgcolor: selectedAnswer === option.value ? 'primary.light' : 'background.paper',
                      color: selectedAnswer === option.value ? 'primary.contrastText' : 'text.primary',
                      border: selectedAnswer === option.value ? '2px solid' : '2px solid transparent',
                      borderColor: selectedAnswer === option.value ? 'primary.dark' : 'transparent',
                      transform: selectedAnswer === option.value ? { xs: 'scale(1)', md: 'scale(1.05)' } : 'scale(1)',
                      '&:hover': {
                        elevation: 4,
                        transform: { xs: 'scale(1)', md: 'scale(1.02)' },
                        bgcolor: selectedAnswer === option.value ? 'primary.main' : 'grey.50',
                      },
                      display: 'flex',
                      flexDirection: { xs: 'row', md: 'column' }, // Horizontal layout on mobile
                      alignItems: 'center',
                      justifyContent: { xs: 'flex-start', md: 'center' },
                      gap: { xs: 2, md: 0 }
                    }}
                    onClick={() => handleAnswerSelect(option.value)}
                  >
                    <Chip
                      label={index + 1}
                      size="small"
                      sx={{
                        mb: { xs: 0, md: 1 },
                        bgcolor: selectedAnswer === option.value ? 'primary.contrastText' : 'primary.main',
                        color: selectedAnswer === option.value ? 'primary.main' : 'primary.contrastText',
                        minWidth: 32,
                      }}
                    />
                    <Typography variant="body1" sx={{ 
                      fontWeight: { xs: 500, md: 600 },
                      flex: { xs: 1, md: 'none' },
                      textAlign: { xs: 'left', md: 'center' },
                      fontSize: { xs: '1rem', md: '1.25rem' }
                    }}>
                      {option.label}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Keyboard Shortcuts Help - Hidden on mobile */}
            <Box sx={{ 
              textAlign: 'center', 
              mb: 3,
              display: { xs: 'none', sm: 'none', md: 'block' } // Hide on mobile and tablets
            }}>
              <Typography variant="caption" color="text.secondary">
                {t('question.keyboard.help')}
              </Typography>
            </Box>

            {/* Status and Back Button */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'center', sm: 'space-between' },
                alignItems: 'center',
                gap: { xs: 2, sm: 0 },
                px: { xs: 2, sm: 0 }
              }}
            >
              <Box sx={{ 
                textAlign: 'center',
                order: { xs: 1, sm: 2 },
                width: { xs: '100%', sm: 'auto' },
                mb: { xs: 2, sm: 0 }
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '0.875rem' } }}>
                  {submitAllResponsesMutation.isLoading 
                    ? t('question.saving') 
                    : currentQuestionIndex + 1 === totalQuestions 
                      ? t('question.final.message')
                      : t('question.select.answer')
                  }
                </Typography>
              </Box>

              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0 || submitAllResponsesMutation.isLoading}
                sx={{ 
                  minWidth: { xs: '100%', sm: 100 },
                  order: { xs: 2, sm: 1 }
                }}
              >
                {t('question.previous')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Progress Bar - placed under the card */}
      <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('question.progress', { current: currentQuestionIndex + 1, total: totalQuestions })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('question.complete.percent', { percent: Math.round(progress) })}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Paper>
    </Box>
  );
};

export default SingleQuestionPage;